import { Edge, getIncomers, getOutgoers, Node, NodeProps, useReactFlow } from 'reactflow';

import { buildPathsFromSchema, filterPropertiesFromSchema, processSourceKeys } from '../../json-schema-viewer/components';
import { resetSourceDataObject, sourceDataObject } from '../../json-schema-viewer/components/SchemaRow/state';
import { uuid } from '../utils';

export function useDeleteNode(id: NodeProps['id']) {
  const { setNodes, setEdges, getNodes, getEdges, getNode } = useReactFlow();

  // Recursive function to get all children (descendants)
  const getAllOutgoers = (node: Node, nodes: Node[], edges: Edge[], allOutgoers: Node[] = []) => {
    const outgoers = getOutgoers(node, nodes, edges);

    // If no more outgoers, return collected outgoers
    if (outgoers.length === 0) return allOutgoers;

    // Add current outgoers to the list
    allOutgoers.push(...outgoers);

    // Recursively get children of outgoers
    outgoers.forEach(outgoer => getAllOutgoers(outgoer, nodes, edges, allOutgoers));

    return allOutgoers;
  };

  const hasSiblings = (id: string, nodes: Node[], edges: Edge[]): boolean => {
    const parentEdge = edges.find(edge => edge.target === id);
    if (!parentEdge) return false;

    const parentNode = getNode(parentEdge.source);
    if (!parentNode) return false;

    const siblingEdges = edges.filter(edge => edge.source === parentEdge.source && edge.target !== id);

    // Evaluator condition
    if (parentNode.type === 'evaluatorNode') {
      const childCount = parentNode.data?.meta_data?.childCount || 0;
      if (childCount === siblingEdges.length + 1) {
        return false;
      }
    }

    return siblingEdges.length > 0;
  }

  const handleDelete = (action: 'configure' | 'delete', deleteSelected: boolean = true) => {
    const nodes = getNodes();
    const edges = getEdges();

    const nodeToProcess = nodes.find(node => node.id === id);
    if (!nodeToProcess) return;

    // Get all outgoing edges (children of the node)
    const outgoers = getAllOutgoers(nodeToProcess, nodes, edges);

    if (action === 'configure') {
      // Re-configure the node and all its children by removing the `nodeConfig` property
      const reconfigureNode = (node: any) => {
        processSourceKeys(node.data?.nodeConfig?.schema.properties, 'remove');
        resetSourceDataObject();
        return node;
      };

      // Update all nodes that need reconfiguration (this node and its outgoers)
      setNodes(
        nodes.map(node =>
          node.id === id || outgoers.some(outgoer => outgoer.id === node.id)
            ? reconfigureNode(node)
            : node
        )
      );
    }

    if (action === 'delete') {
      // Get all nodes to delete (this node and its outgoers)

      const nodesToDelete = deleteSelected ? [nodeToProcess, ...outgoers] : outgoers;
      const requestNode = nodes.find(node => node.type === 'requestNode');
      if (!requestNode) return;

      console.log('====== nodes to delete======', nodesToDelete, requestNode);
      let savedPaths = new Set<string>();

      nodesToDelete.forEach(node => {
        if (node.type !== 'placeholder' && node.data?.nodeConfig) {
          buildPathsFromSchema(node.data?.nodeConfig?.schema, sourceDataObject, node.id, savedPaths);
        }
      });

      console.log('********** saved paths: ', savedPaths);
      const filteredSchema = filterPropertiesFromSchema(requestNode.data?.request_schema, savedPaths);

      console.log('********** filteredSchema: ', filteredSchema);

      const incomers = getIncomers(nodeToProcess, nodes, edges);
      const childPlaceholderId = uuid();

      const childPlaceholderNode = {
        id: childPlaceholderId,
        position: {
          x: (incomers && incomers.length > 0) ? incomers[0]?.position.x : 0,
          y: (incomers && incomers.length > 0) ? incomers[0]?.position.y + 150 : 0,
        },
        type: 'placeholder',
        data: { label: '+' },
      };

      // we need a connection from the clicked node to the new placeholder
      let childPlaceholderEdge: Edge | undefined = undefined;
      if (incomers && incomers.length > 0) {
        childPlaceholderEdge = {
          id: `${incomers[0].id}=>${childPlaceholderId}`,
          source: incomers[0].id,
          target: childPlaceholderId,
          type: 'placeholder',
        };
      }

      const siblingsPresent = hasSiblings(id, nodes, edges);

      // Remove nodes and all edges connected to the nodes
      const newNodes = nodes
        .filter(node => !nodesToDelete.some(n => n.id === node.id))
        .map((node) => {
          let updatedNode = node;
          if (node.type === 'requestNode') {
            updatedNode = { ...node, data: { ...node.data, request_schema: filteredSchema } }
            console.log('updating request node: ', updatedNode)
          }

          return updatedNode;
        })
        .concat((deleteSelected && !siblingsPresent) ? [childPlaceholderNode] : []);

      const newEdges = edges
        .filter(edge => !nodesToDelete.some(node => edge.source === node.id || edge.target === node.id))
        .concat((childPlaceholderEdge && childPlaceholderEdge.id && deleteSelected && !siblingsPresent) ? [childPlaceholderEdge] : []);

      // Update nodes and edges with deleted nodes and edges removed
      setNodes(newNodes);
      setEdges(newEdges);
    }
  };

  return handleDelete;
}

export default useDeleteNode;
